import { Pressable, StyleSheet, TextStyle, View } from "react-native";
import { BodyMediumText } from "./FormattedText";
import { useTheme } from "../hooks/use-theme";
import Icon, { IconName } from "./Icon";
import MarkdownView, { useNormalMarkdownStyle } from "./MarkdownView";
import InfoButton from "./InfoButton";

type BaseLabelValue = {
    label: string;
    value?: string;
    onInfo?: () => void;
    onPress?: () => void;
    icon?: IconName;
    iconColor?: string;
};

type LabelValueTextProps = BaseLabelValue & {
    textStyle?: TextStyle;
    valueStyle?: TextStyle;
    subValues?: BaseLabelValue[];
    isSingleLine?: boolean;
};

export const LabelValueText = ({
    label,
    value,
    onInfo,
    textStyle = {},
    subValues = [],
    valueStyle,
    isSingleLine = false,
    onPress,
    icon,
    iconColor,
}: LabelValueTextProps) => {
    const styles = useStyles();
    const { colors, fonts } = useTheme();

    const markdownTextColor = onPress ? colors.secondary : undefined;

    const defaultMarkdownStyle = useNormalMarkdownStyle({ fontColor: markdownTextColor });

    return (
        <>
            <View style={[styles.wrapper, isSingleLine && { alignItems: "center" }]}>
                <Pressable style={styles.labelWrapper} onPress={onPress} disabled={!onPress}>
                    <MarkdownView
                        markdown={label}
                        fontColor={onPress ? colors.secondary : textStyle.color}
                        initialNumberOfLines={isSingleLine ? 1 : undefined}
                        style={{ flexShrink: 1 }}
                        markdownStyle={{
                            paragraph: {
                                ...fonts.bodyMedium,
                                ...textStyle,
                                color: markdownTextColor,
                            },
                            bullet_list_content: {
                                ...defaultMarkdownStyle.bullet_list_content,
                                ...textStyle,
                                color: markdownTextColor,
                            },
                        }}
                    />
                    {onInfo ? (
                        <InfoButton onPress={onInfo} />
                    ) : icon ? (
                        <Icon
                            style={{ marginLeft: 6 }}
                            name={icon}
                            color={iconColor || colors.secondary}
                            size={18}
                        />
                    ) : null}
                </Pressable>
                <BodyMediumText
                    numberOfLines={1}
                    style={[valueStyle || textStyle, { flexShrink: 0 }]}
                >
                    {value}
                </BodyMediumText>
            </View>
            {subValues?.map((v, i) => (
                <LabelValueText key={i} {...v} textStyle={styles.subValueText} />
            ))}
        </>
    );
};

const useStyles = () => {
    const { fonts, colors, marginSize } = useTheme();

    return StyleSheet.create({
        labelWrapper: {
            alignItems: "center",
            flexDirection: "row",
            // flexBasis: 0 (via flex) bounds the label width: iOS can no longer claim its
            // intrinsic width (full text) and must truncate, keeping the "value" on one line.
            flex: 1,
            paddingRight: marginSize / 1.5,
        },
        wrapper: {
            alignItems: "flex-end",
            flexDirection: "row",
            justifyContent: "space-between",
            width: "100%",
        },
        subValueText: {
            ...fonts.bodySmall,
            color: colors.greyAccessible,
            lineHeight: undefined,
        },
    });
};

export default LabelValueText;
